import { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import DashboardLayout from '../components/DashboardLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { getStoreByOwner, updateStore, checkSubdomainAvailability } from '../lib/firebaseService';
import { Link as LinkIcon, ExternalLink, Copy, Check } from 'lucide-react';

export default function StoreLink() {
  const { firebaseUser } = useAuth();
  const [store, setStore] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);
  const [subdomain, setSubdomain] = useState('');
  const [checkingAvailability, setCheckingAvailability] = useState(false);
  const [isAvailable, setIsAvailable] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (firebaseUser) {
      loadStore();
    }
  }, [firebaseUser]);

  const loadStore = async () => {
    try {
      setPageLoading(true);
      const userStore = await getStoreByOwner(firebaseUser.uid);
      if (userStore) {
        setStore(userStore);
        setSubdomain(userStore.subdomain || '');
      }
    } catch (error) {
      console.error('Mağaza yüklənə bilmədi:', error);
    } finally {
      setPageLoading(false);
    }
  };

  const handleSubdomainChange = (e) => {
    const value = e.target.value;
    const sanitized = value.toLowerCase().replace(/[^a-z0-9-]/g, '');
    setSubdomain(sanitized);
    setIsAvailable(null);
  };

  const handleCheckAvailability = async () => {
    if (!subdomain || subdomain.length < 3) {
      toast.error('Subdomain minimum 3 simvol olmalıdır');
      return;
    }

    setCheckingAvailability(true);
    try {
      const available = await checkSubdomainAvailability(subdomain);
      
      if (store?.subdomain === subdomain) {
        setIsAvailable(true);
        toast.info('Bu sizin mövcud subdomain-inizdir');
      } else {
        setIsAvailable(available);
        if (available) {
          toast.success('Subdomain mövcuddur!');
        } else {
          toast.error('Subdomain artıq istifadə olunub');
        }
      }
    } catch (error) {
      console.error('Yoxlama xətası:', error);
      toast.error('Yoxlama uğursuz oldu');
    } finally {
      setCheckingAvailability(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!store) {
      toast.error('Əvvəlcə mağaza yaradın');
      return;
    }

    if (!subdomain || subdomain.length < 3) {
      toast.error('Subdomain minimum 3 simvol olmalıdır');
      return;
    }

    if (isAvailable === false && store.subdomain !== subdomain) {
      toast.error('Bu subdomain artıq istifadə olunub');
      return;
    }

    setLoading(true);
    try {
      await updateStore(store.id, { subdomain });
      toast.success('Link saxlanıldı');
      loadStore();
    } catch (error) {
      console.error('Xəta:', error);
      toast.error('Əməliyyat uğursuz oldu');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = () => {
    const link = `https://${subdomain}.iveb.az`;
    navigator.clipboard.writeText(link);
    setCopied(true);
    toast.success('Link kopyalandı!');
    setTimeout(() => setCopied(false), 2000);
  };

  if (pageLoading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <p className="text-muted-foreground">Yüklənir...</p>
        </div>
      </DashboardLayout>
    );
  }

  if (!store) {
    return (
      <DashboardLayout>
        <div className="max-w-2xl">
          <div className="bg-white rounded-xl border border-border p-8 text-center">
            <p className="text-muted-foreground mb-4">Əvvəlcə mağaza yaradın</p>
            <Button onClick={() => window.location.href = '/dashboard/store/info'}>
              Mağaza Yarat
            </Button>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  const fullLink = subdomain ? `https://${subdomain}.iveb.az` : '';

  return (
    <DashboardLayout>
      <div className="max-w-3xl">
        <div className="mb-6">
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <LinkIcon className="w-8 h-8" />
            Mağaza Linki
          </h1>
          <p className="text-muted-foreground mt-1">Mağazanızın unikal linkini təyin edin</p>
        </div>

        <div className="bg-white rounded-xl border border-border p-6">
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <Label htmlFor="subdomain">Subdomain *</Label>
              <p className="text-xs text-muted-foreground mb-2">
                Mağazanızın unikal linki. Yalnız kiçik hərflər, rəqəmlər və defis (-). Min 3 simvol.
              </p>
              <div className="flex gap-2">
                <div className="flex-1 flex items-center border border-border rounded-lg overflow-hidden focus-within:ring-2 focus-within:ring-primary">
                  <Input
                    id="subdomain"
                    value={subdomain}
                    onChange={handleSubdomainChange}
                    placeholder="butik-aysel"
                    className="border-0 focus-visible:ring-0 h-11"
                  />
                  <span className="pr-3 text-sm text-muted-foreground">.iveb.az</span>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleCheckAvailability}
                  disabled={!subdomain || subdomain.length < 3 || checkingAvailability}
                  className="h-11"
                >
                  {checkingAvailability ? 'Yoxlanır...' : 'Yoxla'}
                </Button>
              </div>
              
              {isAvailable === true && (
                <p className="text-xs text-green-600 mt-2 flex items-center gap-1">
                  <Check className="w-4 h-4" /> Subdomain mövcuddur!
                </p>
              )}
              {isAvailable === false && (
                <p className="text-xs text-red-600 mt-2">
                  ✗ Bu subdomain artıq istifadə olunub
                </p>
              )}
            </div>

            {fullLink && (
              <div className="p-4 bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg border border-blue-200">
                <p className="text-xs font-medium text-gray-600 mb-2">Mağazanızın linki:</p>
                <div className="flex items-center gap-2">
                  <a
                    href={fullLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary font-semibold hover:underline flex items-center gap-1 flex-1"
                  >
                    {fullLink}
                    <ExternalLink className="w-4 h-4" />
                  </a>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={copyToClipboard}
                    className="h-9"
                  >
                    {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  </Button>
                </div>
              </div>
            )}

            <div className="pt-4">
              <Button 
                type="submit" 
                className="w-full h-11" 
                disabled={loading || !subdomain || subdomain.length < 3}
              >
                {loading ? 'Saxlanılır...' : 'Linki Yadda Saxla'}
              </Button>
            </div>
          </form>
        </div>

        <div className="mt-6 space-y-4">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm text-blue-800">
              <strong>Qeyd:</strong> Subdomain seçdikdən sonra mağazanız <span className="font-mono">{fullLink || 'subdomain.iveb.az'}</span> linkində əlçatan olacaq.
            </p>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <p className="text-sm text-amber-800">
              <strong>Diqqət:</strong> Subdomain dəyişdirdikdən sonra köhnə link işləməyəcək. Müştərilərinizə yeni linki bildirin.
            </p>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
